// src/components/TurfBoxTable.jsx

import React, { useState, useMemo } from 'react';
import { Download, ChevronDown, ChevronUp, TrendingUp, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useLanguage } from '../contexts/LanguageContext';

  const TurfBoxTable = ({ data, onSelectZone }) => {
  const { t, language } = useLanguage();
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';
  const [filterDevice, setFilterDevice] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'device', direction: 'asc' });

  const statusOptions = ['all', 'normal', 'critical'];

  const parseRecordDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date;
  };

  // Benzersiz cihaz ve lokasyonları al
  const devices = useMemo(() => ['all', ...new Set(data.map(d => d.device))], [data]);
  const locations = useMemo(() => ['all', ...new Set(data.map(d => d.location))], [data]);

  // Durum kontrolü
  const getStatusColor = (moisture, temp) => {
    const m = parseFloat(moisture);
    const t = parseFloat(temp);

    if (m < 20 || t > 20) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getStatusText = (moisture, temp) => {
    const m = parseFloat(moisture);
    const t = parseFloat(temp);

    if (m < 20 || t > 20) return 'critical';
    return 'normal';
  };

  // Tarih aralığını hesapla
  const dateRange = useMemo(() => {
    if (data.length === 0) return { min: '', max: '' };
    
    const dates = data
      .map(d => parseRecordDate(d.created_at))
      .filter(Boolean);
    if (dates.length === 0) return { min: '', max: '' };

    const timestamps = dates.map(date => date.getTime());
    const minDate = new Date(Math.min(...timestamps));
    const maxDate = new Date(Math.max(...timestamps));
    
    return {
      min: minDate.toISOString().split('T')[0],
      max: maxDate.toISOString().split('T')[0]
    };
  }, [data]);

  // Tarih filtresini uygula
  const dateFilteredData = useMemo(() => {
    if (!startDate && !endDate) return data;

    return data.filter(record => {
      const recordDate = parseRecordDate(record.created_at);
      if (!recordDate) return false;
      const start = startDate ? new Date(startDate) : new Date('1900-01-01');
      const end = endDate ? new Date(endDate) : new Date('2100-12-31');
      
      // Saat diliminden bağımsız karşılaştırma
      recordDate.setHours(0, 0, 0, 0);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      
      return recordDate >= start && recordDate <= end;
    });
  }, [data, startDate, endDate]);

  // Verileri cihaz ve lokasyon bazında grupla
  const groupedData = useMemo(() => {
    const groups = {};

    dateFilteredData.forEach(record => {
      const key = `${record.device}-${record.location}`;
      const recordDate = parseRecordDate(record.created_at);
      
      if (!groups[key]) {
        groups[key] = {
          device: record.device,
          location: record.location,
          records: [],
          avgAirTemp: 0,
          avgHumidity: 0,
          avgCO2: 0,
          avgSoilMoisture: 0,
          avgSoilTemp: 0,
          avgPH: 0,
          avgEC: 0,
          minMoisture: Infinity,
          maxMoisture: -Infinity,
          minTemp: Infinity,
          maxTemp: -Infinity,
          status: record.status,
          lastUpdate: record.created_at
        };
      }

      groups[key].records.push(record);
      
      // Son güncelleme zamanını kontrol et
      const lastUpdateDate = parseRecordDate(groups[key].lastUpdate);
      if (recordDate && (!lastUpdateDate || recordDate > lastUpdateDate)) {
        groups[key].lastUpdate = record.created_at;
        groups[key].status = record.status;
      }
    });

    // Ortalamaları hesapla
    Object.keys(groups).forEach(key => {
      const group = groups[key];
      const count = group.records.length;

      group.avgAirTemp = (group.records.reduce((sum, r) => sum + parseFloat(r.airTempC || 0), 0) / count).toFixed(1);
      group.avgHumidity = (group.records.reduce((sum, r) => sum + parseFloat(r.humidity || 0), 0) / count).toFixed(1);
      group.avgCO2 = Math.round(group.records.reduce((sum, r) => sum + parseFloat(r.co2 || 0), 0) / count);
      group.avgSoilMoisture = (group.records.reduce((sum, r) => sum + parseFloat(r.soilMoisture || 0), 0) / count).toFixed(1);
      group.avgSoilTemp = (group.records.reduce((sum, r) => sum + parseFloat(r.soilTempC || 0), 0) / count).toFixed(1);
      group.avgPH = (group.records.reduce((sum, r) => sum + parseFloat(r.soilPH || 0), 0) / count).toFixed(1);
      group.avgEC = (group.records.reduce((sum, r) => sum + parseFloat(r.soilEC || 0), 0) / count).toFixed(2);

      // Min/Max
      group.minMoisture = Math.min(...group.records.map(r => parseFloat(r.soilMoisture))).toFixed(1);
      group.maxMoisture = Math.max(...group.records.map(r => parseFloat(r.soilMoisture))).toFixed(1);
      group.minTemp = Math.min(...group.records.map(r => parseFloat(r.soilTempC))).toFixed(1);
      group.maxTemp = Math.max(...group.records.map(r => parseFloat(r.soilTempC))).toFixed(1);

      group.recordCount = count;
      group.statusLabel = getStatusText(group.avgSoilMoisture, group.avgSoilTemp);
    });

    return Object.values(groups);
  }, [dateFilteredData]);

  // Filtreleme ve arama
  const filteredData = useMemo(() => {
    return groupedData.filter(item => {
      const matchesDevice = filterDevice === 'all' || item.device === filterDevice;
      const matchesLocation = filterLocation === 'all' || item.location === filterLocation;
      const matchesStatus = filterStatus === 'all' || item.statusLabel === filterStatus;
      
      return matchesDevice && matchesLocation && matchesStatus;
    });
  }, [groupedData, filterDevice, filterLocation, filterStatus]);

  // Sıralama
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Sayısal değerler için parse et
      if (sortConfig.key.includes('avg') || sortConfig.key.includes('min') || sortConfig.key.includes('max')) {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Sıralama değiştir
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Detay satırını aç/kapat
  const toggleExpand = (key) => {
    setExpandedRow(expandedRow === key ? null : key);
  };

  // Excel'e aktar
  const exportToExcel = () => {
    const exportData = sortedData.map(d => ({
      [t('table.export.device')]: d.device,
      [t('table.export.location')]: d.location,
      [t('table.export.recordCount')]: d.recordCount,
      [t('table.export.avgAirTemp')]: d.avgAirTemp,
      [t('table.export.avgHumidity')]: d.avgHumidity,
      [t('table.export.co2')]: d.avgCO2,
      [t('table.export.avgSoilMoisture')]: d.avgSoilMoisture,
      [t('table.export.minMoisture')]: d.minMoisture,
      [t('table.export.maxMoisture')]: d.maxMoisture,
      [t('table.export.avgSoilTemp')]: d.avgSoilTemp,
      [t('table.export.minTemp')]: d.minTemp,
      [t('table.export.maxTemp')]: d.maxTemp,
      [t('table.export.ph')]: d.avgPH,
      [t('table.export.ec')]: d.avgEC,
      [t('table.export.status')]: t(`table.statuses.${d.statusLabel}`),
      [t('table.export.lastUpdate')]: new Date(d.lastUpdate).toLocaleString(locale)
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('table.export.sheetName'));
    XLSX.writeFile(wb, `${t('table.export.fileNamePrefix')}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Tarih filtresini temizle
  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  // Hızlı tarih filtreleri
  const applyQuickFilter = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-shadow duration-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">
          <a
            href="https://www.proconsport.com/"
            target="_blank"
            rel="noreferrer"
            className="hover:text-slate-700 transition-colors"
          >
            {t('table.title')}
          </a>
        </h2>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-slate-600 font-medium">{t('table.totalGroups')}</div>
            <div className="text-2xl font-bold text-slate-900">{sortedData.length}</div>
            <div className="text-xs text-slate-600 mt-1">{t('table.deviceLocation')}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-slate-600 font-medium">{t('table.totalRecords')}</div>
            <div className="text-2xl font-bold text-slate-900">
              {sortedData.reduce((sum, d) => sum + d.recordCount, 0).toLocaleString(locale)}
            </div>
            <div className="text-xs text-slate-600 mt-1">{t('table.filtered')}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-slate-600 font-medium">{t('table.avgMoisture')}</div>
            <div className="text-2xl font-bold text-slate-900">
              {sortedData.length > 0 ? (sortedData.reduce((sum, d) => sum + parseFloat(d.avgSoilMoisture), 0) / sortedData.length).toFixed(1) : '0'}%
            </div>
            <div className="text-xs text-slate-600 mt-1">{t('table.soilMoisture')}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-slate-600 font-medium">{t('table.avgTemp')}</div>
            <div className="text-2xl font-bold text-slate-900">
              {sortedData.length > 0 ? (sortedData.reduce((sum, d) => sum + parseFloat(d.avgSoilTemp), 0) / sortedData.length).toFixed(1) : '0'}°C
            </div>
            <div className="text-xs text-slate-600 mt-1">{t('table.soilTemp')}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-slate-600 font-medium">{t('table.dateRange')}</div>
            <div className="text-lg font-bold text-slate-900">
              {startDate && endDate ? `${dateFilteredData.length.toLocaleString(locale)}` : t('table.all')}
            </div>
            <div className="text-xs text-slate-600 mt-1">
              {startDate && endDate ? t('table.filteredData') : t('table.noFilter')}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <select
            value={filterDevice}
            onChange={(e) => setFilterDevice(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
          >
            <option value="all">{t('table.allDevices')}</option>
            {devices.slice(1).map(device => (
              <option key={device} value={device}>{device}</option>
            ))}
          </select>

          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
          >
            <option value="all">{t('table.allLocations')}</option>
            {locations.slice(1).map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
          >
            <option value="all">{t('table.all')}</option>
            {statusOptions.slice(1).map(status => (
              <option key={status} value={status}>{t(`table.statuses.${status}`)}</option>
            ))}
          </select>

          <div className="flex flex-col sm:flex-row gap-2 md:justify-end">
            <button
              type="button"
              onClick={() => setIsDateFilterOpen((prev) => !prev)}
              aria-expanded={isDateFilterOpen}
              aria-controls="date-filter-content"
              className="px-4 py-2 border border-gray-300 rounded-lg text-slate-700 hover:bg-gray-100 flex items-center justify-center gap-2 transition-colors duration-200"
            >
              {t('table.dateFilter')}
              {isDateFilterOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            <button
              onClick={exportToExcel}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center justify-center gap-2"
            >
              <Download size={18} />
              {t('table.exportExcel')}
            </button>
          </div>
        </div>

        {isDateFilterOpen && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200" id="date-filter-content">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-700">
                {t('table.dateFilterHint')}
              </div>
              {(startDate || endDate) && (
                <button
                  onClick={clearDateFilter}
                  className="text-xs px-3 py-1 rounded border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                >
                  {t('table.clearFilter')}
                </button>
              )}
            </div>

            {(startDate || endDate) && (
              <div className="mt-3 text-xs text-slate-700 bg-gray-100 px-3 py-2 rounded border border-gray-200">
                <strong>{t('table.activeFilter')}:</strong> {startDate || t('table.noStart')} → {endDate || t('table.noEnd')}
                <span className="ml-2">
                  ({t('table.filterSummary', {
                    filtered: dateFilteredData.length.toLocaleString(locale),
                    total: data.length.toLocaleString(locale)
                  })})
                </span>
              </div>
            )}

            <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              {/* Başlangıç Tarihi */}
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t('table.startDate')}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={dateRange.min}
                  max={endDate || dateRange.max}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
                />
              </div>

              {/* Bitiş Tarihi */}
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t('table.endDate')}
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || dateRange.min}
                  max={dateRange.max}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
                />
              </div>

              {/* Hızlı Filtreler */}
              <div className="md:col-span-6">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t('table.quickFilters')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => applyQuickFilter(1)}
                    className="px-3 py-2 text-xs rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                  >
                    {t('table.last24Hours')}
                  </button>
                  <button
                    onClick={() => applyQuickFilter(7)}
                    className="px-3 py-2 text-xs rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                  >
                    {t('table.last7Days')}
                  </button>
                  <button
                    onClick={() => applyQuickFilter(30)}
                    className="px-3 py-2 text-xs rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                  >
                    {t('table.last30Days')}
                  </button>
                  <button
                    onClick={clearDateFilter}
                    className="px-3 py-2 text-xs rounded-lg border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                  >
                    {t('table.allData')}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-2 text-xs text-slate-600">
              <strong>{t('table.availableRange')}:</strong> {dateRange.min} → {dateRange.max}
            </div>
          </div>
        )}
      </div>

      {/* Grouped Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-shadow duration-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 w-10"></th>
                {[
                  { key: 'device', label: t('table.device') },
                  { key: 'location', label: t('table.location') },
                  { key: 'recordCount', label: t('table.records') },
                  { key: 'avgSoilMoisture', label: t('table.avgMoisture') },
                  { key: 'minMoisture', label: t('table.min') },
                  { key: 'maxMoisture', label: t('table.max') },
                  { key: 'avgSoilTemp', label: t('table.avgTemp') },
                  { key: 'avgPH', label: 'pH' },
                  { key: 'avgEC', label: 'EC' },
                  { key: 'status', label: t('table.status') }
                ].map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="px-4 py-3 text-center text-xs font-semibold text-slate-700 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                  >
                    <div className="flex items-center justify-center gap-1">
                      {col.label}
                      {sortConfig.key === col.key && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700">{t('table.detail')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedData.map((group) => {
                const key = `${group.device}-${group.location}`;
                const isExpanded = expandedRow === key;
                const statusClass = getStatusColor(group.avgSoilMoisture, group.avgSoilTemp);
                const statusText = t(`table.statuses.${group.statusLabel}`);

                return (
                  <React.Fragment key={key}>
                    {/* Main Row */}
                    <tr className={`hover:bg-gray-50 ${isExpanded ? 'bg-gray-100' : ''}`}>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleExpand(key)}
                          className="text-slate-600 hover:text-slate-900 transition-colors duration-200"
                        >
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-semibold text-sm">{group.device}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-1 bg-gray-100 text-slate-700 rounded text-xs font-medium">
                          {group.location}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{group.recordCount}</td>
                      <td className="px-4 py-3 text-sm font-bold text-slate-900">{group.avgSoilMoisture}%</td>
                      <td className="px-4 py-3 text-xs text-slate-600">{group.minMoisture}%</td>
                      <td className="px-4 py-3 text-xs text-slate-600">{group.maxMoisture}%</td>
                      <td className="px-4 py-3 text-sm font-bold text-slate-900">{group.avgSoilTemp}°C</td>
                      <td className="px-4 py-3 text-sm">{group.avgPH}</td>
                      <td className="px-4 py-3 text-sm">{group.avgEC} mS</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold border ${statusClass}`}>
                          {statusText}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => toggleExpand(key)}
                            className="px-3 py-1 rounded text-xs font-medium bg-slate-900 text-white hover:bg-slate-800"
                          >
                            {isExpanded ? t('table.hide') : t('table.detail')}
                          </button>
                          <button
                            type="button"
                            onClick={() => onSelectZone?.(group.location)}
                            className="px-3 py-1 rounded text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-500"
                          >
                            {t('table.sensorMap')}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Detail Rows */}
                    {isExpanded && (
                      <tr>
                        <td colSpan="12" className="px-4 py-4 bg-gray-50">
                          <div className="space-y-4">
                            {/* Summary Info */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                              <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                                <TrendingUp size={18} className="text-slate-600" />
                                {t('table.summaryWithContext', { device: group.device, location: group.location })}
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <div className="text-xs text-slate-600">{t('table.totalMeasurements')}</div>
                                  <div className="text-lg font-bold text-slate-900">{group.recordCount}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-slate-600">{t('table.airTemp')}</div>
                                  <div className="text-lg font-bold text-slate-900">{group.avgAirTemp}°C</div>
                                </div>
                                <div>
                                  <div className="text-xs text-slate-600">{t('table.airHumidity')}</div>
                                  <div className="text-lg font-bold text-slate-900">{group.avgHumidity}%</div>
                                </div>
                                <div>
                                  <div className="text-xs text-slate-600">CO₂</div>
                                  <div className="text-lg font-bold text-slate-900">{group.avgCO2} ppm</div>
                                </div>
                              </div>
                            </div>

                            {/* Detailed Records Table */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                              <h4 className="font-bold text-slate-900 mb-3">
                                {t('table.detailedRecordsWithCount', { count: Math.min(group.records.length, 20) })}
                              </h4>
                              <div className="overflow-x-auto max-h-96">
                                <table className="w-full text-xs">
                                  <thead className="bg-gray-100 sticky top-0">
                                    <tr>
                                      <th className="px-2 py-2 text-center">{t('table.detailHeaders.dateTime')}</th>
                                      <th className="px-2 py-2 text-center">{t('table.detailHeaders.airTemp')}</th>
                                      <th className="px-2 py-2 text-center">{t('table.detailHeaders.airHumidity')}</th>
                                      <th className="px-2 py-2 text-center">CO₂</th>
                                      <th className="px-2 py-2 text-center">{t('table.detailHeaders.wind')}</th>
                                      <th className="px-2 py-2 text-center">{t('table.detailHeaders.lux')}</th>
                                      <th className="px-2 py-2 text-center">{t('table.detailHeaders.soilMoisture')}</th>
                                      <th className="px-2 py-2 text-center">{t('table.detailHeaders.soilTemp')}</th>
                                      <th className="px-2 py-2 text-center">pH</th>
                                      <th className="px-2 py-2 text-center">EC</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {group.records
                                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                      .slice(0, 20)
                                      .map((record, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                          <td className="px-2 py-2 font-mono">
                                            {new Date(record.created_at).toLocaleString(locale)}
                                          </td>
                                          <td className="px-2 py-2">{record.airTempC}°C</td>
                                          <td className="px-2 py-2">{parseFloat(record.humidity).toFixed(1)}%</td>
                                          <td className="px-2 py-2">{Math.round(record.co2)} ppm</td>
                                          <td className="px-2 py-2">{parseFloat(record.windSpeed).toFixed(1)} {t('units.kmh')}</td>
                                          <td className="px-2 py-2">{Math.round(record.lux)}</td>
                                          <td className="px-2 py-2 font-bold text-slate-900">
                                            {parseFloat(record.soilMoisture).toFixed(1)}%
                                          </td>
                                          <td className="px-2 py-2 font-bold text-slate-900">
                                            {record.soilTempC}°C
                                          </td>
                                          <td className="px-2 py-2">{parseFloat(record.soilPH).toFixed(1)}</td>
                                          <td className="px-2 py-2">{parseFloat(record.soilEC).toFixed(2)} mS</td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                              {group.records.length > 20 && (
                                <div className="mt-2 text-xs text-slate-600 text-center">
                                  {t('table.showingFirstRecords', { total: group.records.length })}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {sortedData.length === 0 && (
          <div className="text-center py-12 text-slate-600">
            <AlertCircle className="mx-auto mb-3 text-slate-400" size={48} />
            <p className="text-lg font-medium">{t('table.noRecords')}</p>
            <p className="text-sm">{t('table.changeFilters')}</p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-slate-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-slate-700">
            <p className="font-semibold mb-1">{t('table.howItWorks')}</p>
            <ul className="space-y-1 text-xs">
              <li>{t('table.infoText1')}</li>
              <li>{t('table.infoText2')}</li>
              <li>{t('table.infoText3')}</li>
              <li>{t('table.infoText4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurfBoxTable;
